import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Trophy, XCircle, RotateCcw, Loader2 } from "lucide-react";
import { LOSS_CATEGORIES } from "../lib/lossCategories";
import { fmtMoney } from "../lib/money";

const inp = "border border-[#DFDAD0] px-2 py-1.5 text-sm font-mono outline-none focus:border-[#1C2530] bg-white";
const btn = "flex items-center gap-2 border px-3 py-2 text-xs font-mono uppercase tracking-wider transition-colors disabled:opacity-40";

export default function CloseoutPanel({ lead, user, onChange }) {
  const [mode, setMode] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [value, setValue] = useState("");
  const [cur, setCur] = useState("");
  const [note, setNote] = useState("");
  const [cat, setCat] = useState("");
  const [fxOk, setFxOk] = useState(null);

  useEffect(() => {
    api.get("/tenant/currency").then((r) => {
      setCfg(r.data);
      setCur(r.data.region_currency_defaults?.[lead.region] || r.data.reporting_currency);
    }).catch(() => {});
  }, [lead.region]);

  useEffect(() => {
    if (!cfg || !cur) return;
    if (cur === cfg.reporting_currency) { setFxOk({ ok: true }); return; }
    setFxOk(null);
    api.get(`/money/convert?amount=1&currency=${cur}`)
      .then((r) => setFxOk({ ok: true, rate: r.data.rate_used, date: r.data.rate_date }))
      .catch((e) => setFxOk({ ok: false, msg: e.response?.data?.detail || "No FX rate" }));
  }, [cfg, cur]);

  const canReopen = user.role === "admin" || user.role === "supervisor";
  const closed = lead.status === "won" || lead.status === "lost";
  const co = lead.closeout;

  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); setMode(null); setValue(""); setNote(""); setCat(""); onChange?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
    finally { setBusy(false); }
  };
  const submitWon = () => {
    if (!value || Number(value) <= 0) { toast.error("Deal value is required"); return; }
    run(() => api.post(`/leads/${lead.id}/won`, { deal_value: Number(value), currency: cur || undefined, note: note || undefined }), "Marked Won");
  };
  const submitLost = () => {
    if (!cat) { toast.error("Pick a loss reason"); return; }
    run(() => api.post(`/leads/${lead.id}/lost`, { reason_category: cat, note: note || undefined }), "Marked Lost");
  };
  const reopen = () => {
    if (!window.confirm(`Reopen this lead and reverse the ${co?.kind} close-out?`)) return;
    run(() => api.post(`/leads/${lead.id}/reopen`), "Lead reopened");
  };

  if (closed && co) {
    return (
      <div className="pt-3 border-t border-[#DFDAD0] space-y-2" data-testid="closeout-summary">
        <div className={`font-mono text-[10px] uppercase tracking-wider ${co.kind === "won" ? "text-[#3E8F99]" : "text-[#E50914]"}`} data-testid="closeout-kind">
          {co.kind === "won" ? `Won · ${fmtMoney(co.deal_value, co.currency)}` : `Lost · ${co.reason_category}`} · by {co.by} · {co.at?.slice(0, 10)}
        </div>
        {co.note && <div className="text-xs text-[#1C2530]/70" data-testid="closeout-note">{co.note}</div>}
        {canReopen && (
          <button onClick={reopen} disabled={busy} data-testid="reopen-btn" className={`${btn} border-[#DFDAD0] bg-white hover:bg-[#EFECE5]`}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" strokeWidth={1.5} />} Reopen
          </button>
        )}
      </div>
    );
  }
  if (["won", "lost", "dnc"].includes(lead.status)) return null;

  return (
    <div className="pt-3 border-t border-[#DFDAD0] space-y-3" data-testid="closeout-panel">
      <div className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60">Close-out</div>
      {!mode && (
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setMode("won")} disabled={busy} data-testid="mark-won-btn" className={`${btn} border-[#3E8F99] text-[#3E8F99] bg-white hover:bg-[#3E8F99]/10`}>
            <Trophy className="w-3.5 h-3.5" strokeWidth={1.5} /> Mark Won
          </button>
          <button onClick={() => setMode("lost")} disabled={busy} data-testid="mark-lost-btn" className={`${btn} border-[#E50914] text-[#E50914] bg-white hover:bg-[#E50914]/10`}>
            <XCircle className="w-3.5 h-3.5" strokeWidth={1.5} /> Mark Lost
          </button>
        </div>
      )}
      {mode === "won" && (
        <div className="space-y-2" data-testid="won-form">
          <div className="flex flex-wrap items-center gap-2">
            <input type="number" min="0" step="any" placeholder="Deal value (required)" value={value} onChange={(e) => setValue(e.target.value)} data-testid="won-deal-value-input" className={`${inp} w-44`} />
            <select value={cur} onChange={(e) => setCur(e.target.value)} data-testid="won-currency-select" className={inp}>
              {(cfg?.all_currencies || [cur || "USD"]).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            {cfg && cur && cur !== cfg.reporting_currency && fxOk?.ok && (
              <span className="font-mono text-[10px] text-[#1C2530]/60" data-testid="won-fx-hint">≈ {fmtMoney(Number(value || 0) * fxOk.rate, cfg.reporting_currency)} @ {fxOk.rate} ({fxOk.date})</span>
            )}
          </div>
          {fxOk && !fxOk.ok && (
            <div className="font-mono text-[10px] text-[#E50914]" data-testid="won-fx-missing">{fxOk.msg} — Confirm is disabled until an admin adds the rate.</div>
          )}
          <input placeholder="Close note (optional)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="won-note-input" className={`${inp} w-full`} />
          <div className="flex gap-2">
            <button onClick={submitWon} disabled={busy || !fxOk?.ok} data-testid="won-confirm-btn" className={`${btn} border-[#3E8F99] bg-[#3E8F99] text-white hover:bg-[#357d86]`}>
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trophy className="w-3.5 h-3.5" strokeWidth={1.5} />} Confirm Won
            </button>
            <button onClick={() => setMode(null)} disabled={busy} data-testid="won-cancel-btn" className={`${btn} border-[#DFDAD0] bg-white hover:bg-[#EFECE5]`}>Cancel</button>
          </div>
        </div>
      )}
      {mode === "lost" && (
        <div className="space-y-2" data-testid="lost-form">
          <select value={cat} onChange={(e) => setCat(e.target.value)} data-testid="lost-reason-select" className={`${inp} w-full`}>
            <option value="">Loss reason (required)…</option>
            {LOSS_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <input placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="lost-note-input" className={`${inp} w-full`} />
          <div className="flex gap-2">
            <button onClick={submitLost} disabled={busy} data-testid="lost-confirm-btn" className={`${btn} border-[#E50914] bg-[#E50914] text-white hover:bg-[#c50811]`}>
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <XCircle className="w-3.5 h-3.5" strokeWidth={1.5} />} Confirm Lost
            </button>
            <button onClick={() => setMode(null)} disabled={busy} data-testid="lost-cancel-btn" className={`${btn} border-[#DFDAD0] bg-white hover:bg-[#EFECE5]`}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
